import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import TourNav from "./components/TourNav";
import { TOTAL_SLIDES, renderSlide } from "./slides";

const TOUR_PROGRESS_KEY = "helpgenie:tour:furthest-slide";

const TourPage = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(() => {
    if (typeof window === "undefined") return 0;
    const raw = window.localStorage.getItem(TOUR_PROGRESS_KEY);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    if (Number.isFinite(n) && n >= 0 && n < TOTAL_SLIDES) return n;
    return 0;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem(TOUR_PROGRESS_KEY);
    const prev = raw ? Number.parseInt(raw, 10) : 0;
    if (!Number.isFinite(prev) || index > prev) {
      window.localStorage.setItem(TOUR_PROGRESS_KEY, String(index));
    }
  }, [index]);

  const goTo = useCallback((i: number) => {
    setIndex((current) => {
      const next = Math.max(0, Math.min(TOTAL_SLIDES - 1, i));
      return next === current ? current : next;
    });
  }, []);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  const handleExit = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        prev();
      } else if (e.key === "Escape") {
        handleExit();
      } else if (e.key === "Home") {
        e.preventDefault();
        goTo(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goTo(TOTAL_SLIDES - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, goTo, handleExit]);

  // Scroll active slide to top when navigating
  useEffect(() => {
    const el = document.getElementById(`tour-slide-${index}`);
    if (el) el.scrollTop = 0;
  }, [index]);

  return (
    <>
      <Helmet>
        <title>The Help Genie Tour — Goodbye manuals. Hello Help Genie.</title>
        <meta
          name="description"
          content="A walkthrough of Help Genie: the first scalable support layer for any business. Voice, chat, QR, email — one brain, every channel, 24/7."
        />
      </Helmet>

      <div className="fixed inset-0 bg-[#F9FAFC] overflow-hidden">
        {/* Ambient brand glow (visible on content slides, hidden behind covers) */}
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute -top-40 -left-40 w-[60vw] h-[60vw] rounded-full opacity-25 blur-3xl"
            style={{ background: "radial-gradient(closest-side, #4E9CFF, transparent)" }}
          />
          <div
            className="absolute -bottom-40 -right-40 w-[60vw] h-[60vw] rounded-full opacity-25 blur-3xl"
            style={{ background: "radial-gradient(closest-side, #FF4DB8, transparent)" }}
          />
        </div>

        {/* Exit button — white pill on dark cover slides, otherwise white-on-light */}
        <button
          onClick={handleExit}
          aria-label="Exit tour"
          className="absolute top-5 right-5 z-30 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur border border-[#E5EAF0] px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#222B45] hover:shadow-md transition-all"
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">Exit tour</span>
          <kbd className="hidden md:inline ml-1 text-[10px] font-mono text-gray-400">esc</kbd>
        </button>

        {/* Slide stage */}
        <div className="relative z-10 h-full w-full">
          {Array.from({ length: TOTAL_SLIDES }).map((_, i) => {
            const isActive = i === index;
            return (
              <div
                key={i}
                id={`tour-slide-${i}`}
                aria-hidden={!isActive}
                className="absolute inset-0 h-full w-full transition-all duration-500 ease-out"
                style={{
                  opacity: isActive ? 1 : 0,
                  transform: isActive
                    ? "translateY(0)"
                    : i < index
                      ? "translateY(-2%)"
                      : "translateY(2%)",
                  pointerEvents: isActive ? "auto" : "none",
                }}
              >
                {renderSlide(i, { onStart: next, onExit: handleExit })}
              </div>
            );
          })}
        </div>

        <TourNav
          index={index}
          total={TOTAL_SLIDES}
          onPrev={prev}
          onNext={next}
          onJump={goTo}
        />
      </div>
    </>
  );
};

export default TourPage;
